import React, { useState } from 'react';
import ActionButton from '@westwing/ui-kit/ActionButton';
import { StyledHeaderCentered, StyledPageTitle, StyledPageContent } from '../loginAndRegistration.style';
import { LoginFormProps } from '../LoginForm';
import { RegistrationFormProps } from '../RegistrationForm';
import testIds from '../testId.e2e';
import {
    StyledFormWrapper,
    StyledToggleActions,
    StyledWrapper,
    StyledContainer,
    StyledSeparator,
} from './LoginRegisterWrapper.style';
import useTranslate from 'Hooks/useTranslate';

export interface FormProps<T> {
    headerText: string;
    component: React.FC<T>;
    buttonText: string;
    formProps: T;
}
export const enum Panel {
    LOGIN = 'login',
    REGISTER = 'register',
}
export interface LoginRegisterWrapperProps {
    order?: Panel;
    show?: Panel;
    isLoading: boolean;
    loginProps: FormProps<LoginFormProps>;
    registerProps: FormProps<RegistrationFormProps>;
}

const LoginRegisterWrapper = ({
    order = Panel.LOGIN,
    show = Panel.LOGIN,
    loginProps,
    registerProps,
}: LoginRegisterWrapperProps): JSX.Element => {
    const {
        headerText: loginHeaderText,
        component: LoginForm,
        buttonText: loginCTAText,
        formProps: {
            onSubmit: onLoginSubmit,
            forgotPasswordLink,
            hasApiError: hasLoginApiError,
            setHasApiError: setLoginHasApiError,
            isLoading: isLoginLoading,
        },
    } = loginProps;
    const {
        headerText: registerHeaderText,
        component: RegisterForm,
        buttonText: registerCTAText,
        formProps: {
            onSubmit: onRegisterSubmit,
            hasApiError: hasRegisterApiError,
            setHasApiError: setRegisterHasApiError,
            isLoading: isRegisterLoading,
        },
    } = registerProps;

    const { tPlain } = useTranslate();
    const separator = tPlain('or');

    const [isLoginActive, setIsLoginActive] = useState(show === Panel.LOGIN);
    const handleToggle = () => {
        setIsLoginActive(!isLoginActive);
        setRegisterHasApiError(false);
        if (isLoginActive) {
            setRegisterHasApiError(false);
        } else {
            setLoginHasApiError(false);
        }
    };
    const renderLogin = () => (
        <>
            <StyledHeaderCentered>
                <StyledPageTitle>{loginHeaderText}</StyledPageTitle>
            </StyledHeaderCentered>
            <StyledPageContent>
                <StyledFormWrapper aria-expanded={isLoginActive} data-testid="loginForm">
                    <LoginForm
                        isActive={isLoginActive}
                        hasApiError={hasLoginApiError}
                        onSubmit={onLoginSubmit}
                        forgotPasswordLink={forgotPasswordLink}
                        setHasApiError={setLoginHasApiError}
                        isLoading={isLoginLoading}
                    />
                </StyledFormWrapper>

                <StyledToggleActions>
                    <ActionButton data-testid={testIds.loginFormToggleButton} kind="secondary" onClick={handleToggle}>
                        {loginCTAText}
                    </ActionButton>
                </StyledToggleActions>
            </StyledPageContent>
        </>
    );

    const renderRegister = () => (
        <>
            <StyledHeaderCentered>
                <StyledPageTitle>{registerHeaderText}</StyledPageTitle>
            </StyledHeaderCentered>
            <StyledPageContent>
                <StyledFormWrapper aria-expanded={!isLoginActive} data-testid="registerForm">
                    <RegisterForm
                        isActive={!isLoginActive}
                        hasApiError={hasRegisterApiError}
                        onSubmit={onRegisterSubmit}
                        setHasApiError={setRegisterHasApiError}
                        isLoading={isRegisterLoading}
                    />
                </StyledFormWrapper>

                <StyledToggleActions>
                    <ActionButton
                        data-testid={testIds.registerFormToggleButton}
                        kind="secondary"
                        onClick={handleToggle}
                    >
                        {registerCTAText}
                    </ActionButton>
                </StyledToggleActions>
            </StyledPageContent>
        </>
    );
    return (
        <StyledWrapper>
            <StyledContainer>{order === Panel.LOGIN ? renderLogin() : renderRegister()}</StyledContainer>
            <StyledSeparator>{separator}</StyledSeparator>
            <StyledContainer>{order === Panel.REGISTER ? renderLogin() : renderRegister()}</StyledContainer>
        </StyledWrapper>
    );
};

export default LoginRegisterWrapper;
